import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  Keyboard,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { AppText } from "@/components/ui/AppText";
import { Icon } from "@/components/ui/Icon";
import { Label } from "@/components/ui/Label";
import { AppActionSheet } from "@/components/sheets/ActionSheet";
import { SearchInput } from "@/components/ui/SearchInput";
import { EmptyState } from "@/components/ui/EmptyState";
import type { ActionSheetRef } from "react-native-actions-sheet";
import { useI18n } from "@/context/I18nContext";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { cn } from "@/lib/utils";

export interface SelectOption {
  label: string;
  value: string;
}

export interface SelectFieldProps {
  /** Optional label shown above the trigger. */
  label?: TranslationKeys;
  /** Available options for the picker. */
  options: SelectOption[];
  /** Currently selected value. */
  value?: string;
  /** Called when the user picks an option. */
  onChange: (value: string) => void;
  /** Placeholder shown when no option is selected. */
  placeholder?: TranslationKeys;
  disabled?: boolean;
  /** Validation error message displayed below the trigger. */
  error?: TranslationKeys;
  /** Hint message displayed below the trigger (hidden when `error` is set). */
  hint?: TranslationKeys;
  /** Title displayed in the picker modal header. */
  modalTitle?: TranslationKeys;
  /** Forwarded to the trigger element for testing. */
  testID?: string;
  /** Screen-reader label (translation key). Defaults to `label`. */
  accessibilityLabel?: TranslationKeys;
  /** When true, renders a red asterisk after the label. */
  required?: boolean;
  /** When true, shows a search input at the top of the picker sheet. */
  searchable?: boolean;
  isRTL?: boolean;
}

// ─── Style helpers ───────────────────────────────────────────────────────────

function triggerClass(hasError: boolean, disabled: boolean): string {
  const base = "flex-row items-center justify-between px-3 py-3 rounded-md border";
  if (disabled) return `${base} bg-stroke-20 border-stroke-50`;
  if (hasError) return `${base} bg-white border-error`;
  return `${base} bg-white border-stroke-50`;
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Dropdown / select field matching the currency picker in the Figma financial
 * settlement step (e.g. "CNY ¥ Chinese Yuan").
 *
 * Renders as a TextInput-like trigger with a trailing chevron icon. Tapping
 * opens a bottom-sheet Modal listing all options. A selected option closes the
 * modal and updates the trigger display.
 *
 * Supports label / hint / error display matching the rest of the form fields.
 *
 * @example
 * const [currency, setCurrency] = useState('CNY');
 *
 * <SelectField
 *   label="Settlement Currency"
 *   options={[
 *     { label: 'CNY ¥ Chinese Yuan', value: 'CNY' },
 *     { label: 'USD $ US Dollar',    value: 'USD' },
 *   ]}
 *   value={currency}
 *   onChange={setCurrency}
 *   placeholder="Select a currency"
 * />
 */
export const SelectField = memo(function SelectField({
  label,
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  hint,
  modalTitle,
  testID,
  isRTL,
  searchable = false,
  accessibilityLabel,
  required,
}: SelectFieldProps) {
  const { t, isRTL: contextIsRTL } = useI18n();
  const resolvedIsRTL = isRTL ?? contextIsRTL;
  const isWide = useIsWideScreen();
  const { height: windowHeight } = useWindowDimensions();
  const actionSheetRef = useRef<ActionSheetRef>(null);
  const triggerRef = useRef<View>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{
    triggerTop: number;
    triggerBottom: number;
    left: number;
    width: number;
  } | null>(null);
  const [keyboardTop, setKeyboardTop] = useState<number | null>(null);

  useEffect(() => {
    if (!isWide) {
      setDropdownOpen(false);
      setDropdownPos(null);
      return;
    }
  }, [isWide]);

  useEffect(() => {
    if (Platform.OS === "web" || !isWide) return;
    const show = Keyboard.addListener("keyboardDidShow", (e) => {
      setKeyboardTop(e.endCoordinates.screenY);
      triggerRef.current?.measureInWindow((x, y, w, h) => {
        if (dropdownOpen)
          setDropdownPos({ triggerTop: y, triggerBottom: y + h, left: x, width: w });
      });
    });
    const hide = Keyboard.addListener("keyboardDidHide", () => {
      setKeyboardTop(null);
      triggerRef.current?.measureInWindow((x, y, w, h) => {
        if (dropdownOpen)
          setDropdownPos({ triggerTop: y, triggerBottom: y + h, left: x, width: w });
      });
    });
    return () => {
      show.remove();
      hide.remove();
    };
  }, [isWide, dropdownOpen]);

  const resolvedPlaceholder = placeholder ? t(placeholder) : t("common.selectOption");
  const selectedOption = options.find((o) => o.value === value);
  const displayText = selectedOption?.label ?? resolvedPlaceholder;
  const hasValue = selectedOption != null;
  const hasError = error != null && error.length > 0;

  const openPicker = useCallback(() => {
    if (disabled) return;
    setSearchQuery("");
    if (isWide) {
      triggerRef.current?.measureInWindow((x, y, w, h) => {
        setDropdownPos({ triggerTop: y, triggerBottom: y + h, left: x, width: w });
        setDropdownOpen(true);
      });
    } else {
      actionSheetRef.current?.show();
    }
  }, [disabled, isWide]);

  const closePicker = useCallback(() => {
    actionSheetRef.current?.hide();
  }, []);

  // ─── Tablet panel placement ────────────────────────────────────────────────
  const _effectiveBottom = keyboardTop ?? windowHeight;
  const _spaceBelow = _effectiveBottom - (dropdownPos?.triggerBottom ?? 0) - 16;
  const _spaceAbove = (dropdownPos?.triggerTop ?? 0) - 16;
  const _flipUp = _spaceBelow < 120 && _spaceAbove > _spaceBelow;
  const _panelMaxH = Math.min(360, Math.max(0, _flipUp ? _spaceAbove : _spaceBelow));

  return (
    <View className="w-full mb-4 items-start">
      {/* ── Label ──────────────────────────────────────────────── */}
      {label != null && <Label label={label} required={required} />}

      {/* ── Trigger ────────────────────────────────────────────── */}
      <View ref={triggerRef} className="w-full">
        <TouchableOpacity
          testID={testID}
          onPress={openPicker}
          disabled={disabled}
          activeOpacity={0.8}
          accessibilityRole="combobox"
          accessibilityLabel={t(accessibilityLabel ?? label ?? "common.selectOption")}
          accessibilityState={{ disabled, expanded: isWide ? dropdownOpen : undefined }}
          accessibilityValue={{ text: selectedOption?.label }}
          className={triggerClass(hasError, disabled)}
        >
          <AppText
            weight="medium"
            className={`flex-1 text-base ${hasValue ? "text-dark-text-50" : "text-dark-text-10"}`}
            numberOfLines={1}
            style={{ textAlign: resolvedIsRTL ? "right" : "left" }}
          >
            {displayText}
          </AppText>
          {/* Chevron icon */}
          <Icon
            as={Feather}
            name="chevron-down"
            size={18}
            className={cn(disabled ? "text-dark-text-20" : "text-supporting-text")}
          />
        </TouchableOpacity>
      </View>

      {/* ── Feedback text ──────────────────────────────────────── */}
      {hasError ? (
        <AppText weight="medium" className="text-xs text-error mt-1 text-start" role="alert">
          {t(error!)}
        </AppText>
      ) : hint != null ? (
        <AppText weight="medium" className="text-xs text-dark-text-10 mt-1 text-start">
          {t(hint)}
        </AppText>
      ) : null}

      {/* ── Tablet dropdown ────────────────────────────────────── */}
      {isWide && (
        <Modal
          visible={dropdownOpen}
          transparent
          animationType="none"
          onRequestClose={() => {
            setDropdownOpen(false);
            setSearchQuery("");
          }}
          statusBarTranslucent
        >
          <View style={{ flex: 1 }}>
            {/* Backdrop — taps outside the panel close it */}
            <Pressable
              testID={testID ? `${testID}-backdrop` : "select-backdrop"}
              style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
              onPress={() => {
                setDropdownOpen(false);
                setSearchQuery("");
              }}
            />
            {/* Panel rendered after backdrop → higher z-order → absorbs all taps */}
            {dropdownPos && (
              <View
                style={{
                  position: "absolute",
                  top: _flipUp
                    ? dropdownPos.triggerTop - _panelMaxH - 4
                    : dropdownPos.triggerBottom + 4,
                  bottom: _flipUp ? windowHeight - dropdownPos.triggerTop + 4 : undefined,
                  left: dropdownPos.left,
                  width: dropdownPos.width,
                  zIndex: 1,
                  backgroundColor: "white",
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: "#E5E7EB",
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.08,
                  shadowRadius: 8,
                  elevation: 8,
                  overflow: "hidden",
                  maxHeight: _panelMaxH,
                  minHeight: 200,
                }}
              >
                {searchable && (
                  <View className="px-3 pt-3 pb-2">
                    <SearchInput
                      value={searchQuery}
                      onChangeText={setSearchQuery}
                      placeholder="common.search"
                    />
                  </View>
                )}
                <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator>
                  {options
                    .filter((o) =>
                      searchable && searchQuery.length > 0
                        ? o.label.toLowerCase().includes(searchQuery.toLowerCase())
                        : true
                    )
                    .map((option) => {
                      const isSelected = option.value === value;
                      return (
                        <Pressable
                          key={option.value}
                          onPress={() => {
                            setDropdownOpen(false);
                            setSearchQuery("");
                            onChange(option.value);
                          }}
                          accessibilityRole="button"
                          accessibilityState={{ selected: isSelected }}
                          className={`flex-row items-center justify-between px-4 py-3 border-b border-stroke-50 ${
                            isSelected ? "bg-blue-5" : ""
                          }`}
                        >
                          <AppText
                            weight={isSelected ? "semibold" : "regular"}
                            className={cn(
                              "text-base",
                              isSelected ? "text-dark-text-50" : "text-supporting-text"
                            )}
                          >
                            {option.label}
                          </AppText>
                          {isSelected && (
                            <Icon as={Feather} name="check" size={14} className="text-primary" />
                          )}
                        </Pressable>
                      );
                    })}
                  {searchable &&
                    searchQuery.length > 0 &&
                    options.filter((o) => o.label.toLowerCase().includes(searchQuery.toLowerCase()))
                      .length === 0 && <EmptyState title="multiSelectPicker.noResults" />}
                </ScrollView>
              </View>
            )}
          </View>
        </Modal>
      )}

      {/* ── Mobile bottom sheet ────────────────────────────────── */}
      <AppActionSheet
        ref={actionSheetRef}
        headerTitle={modalTitle ?? label ?? "common.selectOption"}
        onClose={() => setSearchQuery("")}
      >
        {searchable && (
          <View className="px-4 pb-2">
            <SearchInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder="common.search"
            />
          </View>
        )}
        <ScrollView
          keyboardShouldPersistTaps="handled"
          style={{ maxHeight: 400 }}
          showsVerticalScrollIndicator
        >
          <View className="pb-4">
            {options
              .filter((o) =>
                searchable && searchQuery.length > 0
                  ? o.label.toLowerCase().includes(searchQuery.toLowerCase())
                  : true
              )
              .map((option) => {
                const isSelected = option.value === value;
                return (
                  <Pressable
                    key={option.value}
                    onPress={() => {
                      closePicker();
                      onChange(option.value);
                    }}
                    accessibilityRole="button"
                    accessibilityState={{ selected: isSelected }}
                    className={`px-4 py-3 border-b border-stroke-50 ${isSelected ? "bg-blue-5" : ""}`}
                  >
                    <AppText
                      weight={isSelected ? "semibold" : "regular"}
                      className="text-base text-dark-text-50"
                      style={{ textAlign: resolvedIsRTL ? "right" : "left" }}
                    >
                      {option.label}
                    </AppText>
                  </Pressable>
                );
              })}
          </View>
        </ScrollView>
      </AppActionSheet>
    </View>
  );
});
